/**
 * The browser windows of Connect that are closed (I-3641), for the add menu of
 * the tab bar and the start page: each comes back in a browser tab of its own.
 */
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAccountConfig } from "@blpde/connect";
import { storedTabLabel } from "./tileLabels";
import {
  closedContent,
  closedWindows,
  forgetWindowsPatch,
  liveWindows,
  readWindowId,
  windowUrl,
  type ClosedWindow,
} from "./tileWindows";
import type { StoredTiles } from "./useTileWorkspaces";

export function useClosedWindows() {
  const { t } = useI18n();
  const shellConfig = useAccountConfig<{ tiles?: StoredTiles }>("shell");
  const closed = ref<ClosedWindow[]>([]);

  async function load(): Promise<void> {
    // Another window may have closed since this one read its config.
    await shellConfig.reload();
    const open = await liveWindows();
    const own = readWindowId();
    if (own) open.add(own);
    const stored = shellConfig.effective.value?.tiles;
    closed.value = closedWindows(stored, open, (id) =>
      storedTabLabel(stored, id, t),
    );
  }

  const clock = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });
  const date = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  function when(savedAt: string): string {
    if (!savedAt) return "";
    const at = new Date(savedAt);
    const days = Math.round(
      (new Date().setHours(0, 0, 0, 0) - new Date(at).setHours(0, 0, 0, 0)) /
        (24 * 60 * 60 * 1000),
    );
    if (days === 0)
      return t("connect.tiles.closedToday", { time: clock.format(at) });
    if (days === 1)
      return t("connect.tiles.closedYesterday", { time: clock.format(at) });
    return date.format(at);
  }

  /** „Heute, 12:43 · 2 Tabs“ */
  function detail(entry: ClosedWindow): string {
    const count = entry.tabs.length;
    return [
      when(entry.savedAt),
      t("connect.tiles.tabCount", { n: count }, count),
    ]
      .filter(Boolean)
      .join(" · ");
  }

  function label(entry: ClosedWindow): string {
    return `${closedContent(entry)} · ${detail(entry)}`;
  }

  function reopen(id: string): void {
    window.open(windowUrl(id), "_blank");
  }

  /** The list is empty afterwards; they cannot come back. */
  async function forgetAll(): Promise<void> {
    // One of them may have come back since the list was made. A window busy loading
    // answers late, and what is forgotten here does not come back.
    const open = await liveWindows(1000);
    const stored = shellConfig.effective.value?.tiles;
    const ids = closed.value
      .map((entry) => entry.id)
      .filter((id) => !open.has(id));
    closed.value = [];
    if (ids.length)
      void shellConfig.patch({
        tiles: forgetWindowsPatch(stored, ids) as StoredTiles,
      });
  }

  return {
    closed,
    load,
    title: closedContent,
    detail,
    label,
    reopen,
    forgetAll,
  };
}
